/**
 * React Query hooks for the data-for-points Activities admin API (plan 1904).
 *
 * Calls zaviago_backend.rewards.activities.admin_api — a DIFFERENT module from
 * rewards.admin_api. Company is derived from the admin session on the backend;
 * loyalty_program is auto-resolved on create (GAP-B) — the frontend never sends
 * it. frappeMethod<T> already unwraps the Python `.message`, and a backend
 * frappe.throw() surfaces as `err.message` (powers the duplicate-rule toast).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { useRewardsStoreKey } from "./rewardsAdminHooks";

const ACTIVITY_API = "zaviago_backend.rewards.activities.admin_api";

// ---------------------------------------------------------------------------
// Types — match the Python return shapes exactly
// ---------------------------------------------------------------------------

export interface ActivityLabels {
  page_label?: string;
  section_label?: string;
  action_label?: string;
}

export interface ActivityRule {
  name: string;
  page: string;
  section: string;
  action: string;
  points: number;
  max_per_customer: number;
  /** Epic 09 repeatability: 1 = once per customer, 0 = repeatable per occurrence */
  one_time_use: number;
  /** Epic 09: total points ceiling per customer from this rule (0 = unlimited) */
  max_points_per_customer: number;
  enabled: number;
  valid_from: string | null;
  valid_to: string | null;
  promo_message: string | null;
  /** Always present from the backend: {page_label,section_label,action_label} or {} */
  labels: ActivityLabels;
}

export interface ActivityRulesResponse {
  rules: ActivityRule[];
}

export interface RegistryAction {
  key: string;
  label: string;
  /** Epic 09: when null, this activity cannot repeat (toggle stays One-time). */
  reference_source?: string | null;
}
export interface RegistrySection {
  key: string;
  label: string;
  actions: RegistryAction[];
}
export interface RegistryPage {
  key: string;
  label: string;
  sections: RegistrySection[];
}
export interface RegistryTree {
  pages: RegistryPage[];
}

export const activityQueryKeys = {
  rules: ["activities", "rules"] as const,
  registry: ["activities", "registry"] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useActivityRules(enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<ActivityRulesResponse>({
    queryKey: [...activityQueryKeys.rules, sk],
    queryFn: () =>
      frappeMethod<ActivityRulesResponse>(`${ACTIVITY_API}.get_activity_rules`, { storefront_key: sk }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useActivityRegistry(enabled = true) {
  return useQuery<RegistryTree>({
    queryKey: activityQueryKeys.registry,
    queryFn: () =>
      frappeMethod<RegistryTree>(`${ACTIVITY_API}.get_activity_registry`),
    staleTime: 1000 * 60 * 60, // registry rarely changes
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations — each invalidates the rules list so the tab refreshes
// ---------------------------------------------------------------------------

export function useCreateActivity() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      page: string;
      section: string;
      action: string;
      points: number;
      max_per_customer?: number;
      one_time_use?: number;
      max_points_per_customer?: number;
      valid_from?: string | null;
      valid_to?: string | null;
      promo_message?: string | null;
    }) =>
      frappeMethod<{ success: boolean; name: string }>(
        `${ACTIVITY_API}.create_activity_rule`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityQueryKeys.rules });
    },
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      name: string;
      points?: number;
      max_per_customer?: number;
      one_time_use?: number;
      max_points_per_customer?: number;
      valid_from?: string | null;
      valid_to?: string | null;
      promo_message?: string | null;
      enabled?: number;
    }) =>
      frappeMethod<{ success: boolean; name: string }>(
        `${ACTIVITY_API}.update_activity_rule`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityQueryKeys.rules });
    },
  });
}

export function useToggleActivity() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: { name: string; enabled: number }) =>
      frappeMethod<{ success: boolean; name: string; enabled: number }>(
        `${ACTIVITY_API}.toggle_activity_rule`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityQueryKeys.rules });
    },
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: { name: string }) =>
      frappeMethod<{ success: boolean }>(
        `${ACTIVITY_API}.delete_activity_rule`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityQueryKeys.rules });
    },
  });
}
